import {
  personAgeRanges,
  parseBounds,
  type RangeDefinition,
} from './search-filters';

export interface AiRange { min?: number | null; max?: number | null }
export interface BackendAiNote {
  code: string;
  params?: Record<string, unknown>;
}
export type AiNote =
  | { kind: 'rounded'; field: string; requested: AiRange; applied: AiRange }
  | { kind: 'dropped'; field: string }
  | { kind: 'sort_unsupported' }
  | { kind: 'unmapped'; text: string }
  | { kind: 'backend'; code: BackendAiNote['code']; params?: Record<string, unknown> };

export interface AiInterpretation {
  filters: {
    q?: string; industries?: string[]; nace_codes?: string[];
    bundeslaender?: string[]; cities?: string[]; country?: 'DE' | 'CH' | 'AT' | 'all';
    rechtsform_codes?: (string | number)[]; revenue?: AiRange; total_assets?: AiRange;
    profit?: AiRange; employees?: AiRange; person_age?: AiRange;
    founded_from_year?: number; founded_to_year?: number;
    legal_status?: string[]; company_size?: string[]; tender_role?: string[];
    memberships?: string[]; family_managed?: boolean; founder_led?: boolean;
    succession_risk?: boolean; has_website?: boolean; has_email?: boolean;
    person_name?: string; financial_year?: number;
    sort_by?: 'relevance' | 'revenue' | 'total_assets' | 'employees' | 'founded_newest' | 'founded_oldest';
  };
  unmapped: string[];
  notes: BackendAiNote[];
  prompt_version: string;
  model: string;
}

export function bucketsCovering(min: number | null | undefined, max: number | null | undefined, ladder: readonly RangeDefinition[]) {
  const lower = min ?? -Infinity;
  const upper = max ?? Infinity;
  return lower >= upper ? [] : ladder.filter((bucket) =>
    (bucket.max ?? Infinity) > lower && (bucket.min ?? -Infinity) < upper,
  );
}

/** Canonical filter identity, independent of URL presentation and value order. */
export function aiSearchKey(params: URLSearchParams): string {
  const result = new URLSearchParams();
  const ignored = new Set(['sort', 'offset', 'limit']);
  for (const key of [...new Set(params.keys())].sort()) {
    if (ignored.has(key)) continue;
    const values = params.getAll(key);
    if (key === 'country' && values.every((value) => value.toUpperCase() === 'DE')) continue;
    values.sort().forEach((value) => result.append(key, value));
  }
  return result.toString();
}

export function interpretationToSearchParams(
  interp: AiInterpretation,
  opts: { currentYear?: number } = {},
): { params: URLSearchParams; notes: AiNote[] } {
  const params = new URLSearchParams();
  const notes: AiNote[] = (interp.notes ?? []).map((note) => ({ kind: 'backend', ...note }));
  interp.unmapped?.forEach((text) => notes.push({ kind: 'unmapped', text }));
  const f = interp.filters;
  const scalar = (key: string, value: string | number | undefined) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  };
  const repeated = (key: string, values?: string[]) => values?.forEach((value) => params.append(key, value));
  scalar('q', f.q);
  scalar('person_name', f.person_name);
  repeated('industry', f.industries);
  repeated('naceCode', f.nace_codes);
  repeated('Bundesland', f.bundeslaender);
  repeated('city', f.cities);
  if (f.country && f.country !== 'DE') scalar('country', f.country);
  f.rechtsform_codes?.forEach((code) => params.append('rechtsformCode', String(code)));
  // Financial bounds are exact integers; only person age still uses buckets.
  for (const [metric, requested] of [['revenue', f.revenue], ['aktiva', f.total_assets], ['profit', f.profit], ['employees', f.employees]] as const) {
    if (!requested) continue;
    const bounds = parseBounds(requested.min, requested.max);
    scalar(`${metric}Min`, bounds.min);
    scalar(`${metric}Max`, bounds.max);
  }
  if (f.person_age) {
    const year = opts.currentYear ?? new Date().getFullYear();
    const years = { min: f.person_age.max == null ? null : year - f.person_age.max,
      max: f.person_age.min == null ? null : year - f.person_age.min };
    const picked = bucketsCovering(years.min, years.max, personAgeRanges(year));
    if (!picked.length) notes.push({ kind: 'dropped', field: 'person_age' });
    else {
      picked.forEach(({ id }) => params.append('ageRange', id));
      const yearMin = Math.min(...picked.map((bucket) => bucket.min ?? -Infinity));
      const yearMax = Math.max(...picked.map((bucket) => bucket.max ?? Infinity));
      const applied = { min: yearMax === Infinity ? null : year - yearMax,
        max: yearMin === -Infinity ? null : year - yearMin };
      if ((applied.min ?? null) !== (f.person_age.min ?? null) || (applied.max ?? null) !== (f.person_age.max ?? null)) {
        notes.push({ kind: 'rounded', field: 'person_age', requested: f.person_age, applied });
      }
    }
  }
  scalar('foundedAtFrom', f.founded_from_year);
  scalar('foundedAtTo', f.founded_to_year);
  repeated('legalStatus', f.legal_status);
  repeated('companySize', f.company_size);
  repeated('tenderRole', f.tender_role);
  repeated('membership', f.memberships);
  for (const [key, value] of [['familyManaged', f.family_managed], ['founderLed', f.founder_led], ['successionRisk', f.succession_risk]] as const) {
    if (value !== undefined) scalar(key, String(value));
  }
  if (f.has_website) scalar('hasWebsite', 'true');
  if (f.has_email) scalar('hasEmail', 'true');
  scalar('year', f.financial_year);
  const sorts = { relevance: 'relevance', revenue: 'revenue-desc', total_assets: 'aktiva-desc', employees: 'employee_count-desc' } as const;
  if (f.sort_by === 'founded_newest' || f.sort_by === 'founded_oldest') notes.push({ kind: 'sort_unsupported' });
  else if (f.sort_by) scalar('sort', sorts[f.sort_by]);
  return { params, notes };
}
